import React, { FormEventHandler } from 'react';

import { useUpdateCardMutation } from '@shared/welibrary-graphql/content_card/mutations.hook';

import useModal from '@components/modals/hooks/useModal';
import useDialogState from '@components/content/newpost/forms/Dialog/hooks/useDialogState';

import ModalLoading from '@components/modals/ModalLoading';
import DialogForm from '@components/content/newpost/forms/Dialog/DialogForm';

import { ContentCard } from '@shared/welibrary-graphql/types';

import getLogger from '@core/logger';

const logger = getLogger(module);

type UpdateDialogFormProps = {
    hasAdminRole?: boolean;
    card: Partial<ContentCard>;
};

const UpdateDialogForm: React.FC<UpdateDialogFormProps> = ({ hasAdminRole, card }) => {
    const [state, setState] = useDialogState(card);

    const { newModal, closeAllModals } = useModal();

    const [updateCard] = useUpdateCardMutation();

    const onSubmit: FormEventHandler<HTMLFormElement> = e => {
        e.preventDefault();

        const {
            main: { event, people, dialogs },
            settings: { displayInNewsfeed, rankOrder },
        } = state;

        const updatePayload = {
            cardID: card?._id,
            // If number is removed from rankOrder, set it to null
            rankOrder: rankOrder === false ? null : rankOrder,
            displayInNewsfeed,
            dialogSettings: { event, people, dialogs },
        };

        // Get inherited setting, in this case assumes it will be the chapter for a 'normal' card
        const inheritedSettings = card?.reference?.chapterSource?.library_settings;

        if (inheritedSettings) {
            try {
                Object.entries(inheritedSettings).forEach(([key, value]) => {
                    if (key in updatePayload) {
                        // TS is having trouble inferring that key is a valid key for updatePayload,
                        // so this explicitly sets that to make the compiler happy
                        const typeSafeKey = key as keyof typeof updatePayload;

                        // This removes the property from the update payload if it matches the inherited settings
                        if (updatePayload[typeSafeKey] && value === updatePayload[typeSafeKey]) {
                            delete updatePayload[typeSafeKey];
                        }
                    }
                });
            } catch (error) {
                logger.error('error updating card', e);
            }
        }

        updateCard({ variables: updatePayload }).then(closeAllModals);

        newModal(<ModalLoading message="Updating Dialog" />, { widen: true, addShadow: true });
    };

    return (
        <DialogForm
            state={state}
            setState={setState}
            onSubmit={onSubmit}
            hasAdminRole={hasAdminRole}
            title="Edit Dialog"
            buttonText="Update"
        />
    );
};

export default UpdateDialogForm;
